import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { User } from './types/User';
import { useUserContext } from '~/context/UserProvider';
import { useMutation } from '@tanstack/react-query';
import { deactivateUser, activateUser } from '~/services/users';

interface Props {}

const UserProfileTitlePanel: React.FC<Props> = () => {
    const { user, userDispatch } = useUserContext();

    const deactivate = useMutation({
        mutationFn: () => {
            return deactivateUser(user?.id);
        },
        onSuccess: (data) => {
            // Handle success, e.g., show a success message or refetch user data
            userDispatch({
                type: 'set_user',
                payload: data,
            });
            toast.success('User deactivated');
        },
        onError: (error) => {
            // Handle error, e.g., show an error message
            console.error('Error deactivating user:', error);
        },
    });

    const activate = useMutation({
        mutationFn: () => {
            return activateUser(user?.id);
        },
        onSuccess: (data) => {
            // Handle success, e.g., show a success message or refetch user data
            userDispatch({
                type: 'set_user',
                payload: data,
            });
            toast.success('User activated');
        },
        onError: (error) => {
            // Handle error, e.g., show an error message
            console.error('Error activating user:', error);
        },
    });

    return (
        <>
            <div className='flex justify-between'>
                <div>
                    <div className='flex'>
                        <h1 className='text-[38px]/[44px]'>
                            {user?.person?.first_name} {user?.person?.last_name}
                        </h1>
                        <div className='ml-[15px] mt-[10px] w-[16px]'>
                            <svg
                                fill='#00AEB3'
                                width='20px'
                                height='20px'
                                viewBox='0 0 32 32'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <circle cx='16' cy='16' r='16' />
                            </svg>
                        </div>
                    </div>

                    <div className='flex'>
                        {user?.is_active ? (
                            <div className='inline-flex rounded-3xl bg-[#00AEB3] px-2 py-1 text-[12px]/[14.4px] text-[#131212]'>
                                Active
                            </div>
                        ) : (
                            <div className='inline-flex rounded-3xl bg-[#F02D3A] px-2 py-1 text-[12px]/[14.4px] text-[#131212]'>
                                Inactive
                            </div>
                        )}
                        {user?.is_staff && (
                            <div className='ml-[8px] inline-flex rounded-3xl bg-[#FEBF3E] px-2 py-1 text-[12px]/[14.4px] text-[#131212]'>
                                Staff
                            </div>
                        )}
                        {user?.is_superuser && (
                            <div
                                className='ml-[8px] inline-flex rounded-3xl bg-[#7C3AED] px-2 py-1 text-[12px]/[14.4px] text-white'
                                title="Superuser access, including the ability to view any client's portal via campaign impersonation."
                            >
                                Super Admin
                            </div>
                        )}
                        <div className='ml-[15px] text-[16px]/[24px] text-[#949599]'>
                            {user?.last_login && new Date(user.last_login).getFullYear() > 1970 && (
                                <>
                                    Last Login:{' '}
                                    {format(user.last_login, 'LL/dd/yy')}{' '}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className='flex'>
                    {user?.is_active ? (
                        <button
                            className='m-auto mr-3 rounded-3xl bg-[#F02D3A] px-[24px] py-[7px] text-[14px]/[18px] font-bold text-black'
                            onClick={() => deactivate.mutate()}
                        >
                            DEACTIVATE
                        </button>
                    ) : (
                        <button
                            className='m-auto mr-3 rounded-3xl bg-[#00AEB3] px-[24px] py-[7px] text-[14px]/[18px] font-bold text-black'
                            onClick={() => activate.mutate()}
                        >
                            REACTIVATE
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserProfileTitlePanel;
